import { useParams, Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Circle,
  Sparkles,
  Lightbulb,
  LinkIcon,
  Play,
  HelpCircle,
  BookOpen,
  Brain,
  Eye,
  Users,
  Shield,
  Network as NetworkIcon,
  Landmark,
  Cpu,
  Compass,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { getLessonById, getAdjacentLesson, getModuleById, CURRICULUM } from '@/lib/curriculum';
import { useProgress } from '@/hooks/useProgress';
import { cn } from '@/lib/utils';

const CURRICULUM_FLAT = CURRICULUM.flatMap((m) =>
  m.lessons.map((l) => ({ ...l, moduleId: m.id }))
);

const MODULE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Eye, Brain, Users, Shield, NetworkIcon, Landmark, Cpu, Compass,
};

const REGISTER_STYLES: Record<string, { label: string; color: string }> = {
  empirical: { label: 'Empirical', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  philosophical: { label: 'Philosophical', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' },
  contemplative: { label: 'Contemplative', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
};

const LessonPage = () => {
  const { moduleId, lessonId } = useParams<{ moduleId: string; lessonId: string }>();
  const { module: mod, lesson } = getLessonById(moduleId || '', lessonId || '');
  const { isLessonComplete, toggleLessonComplete } = useProgress();
  const adjacent = getAdjacentLesson(moduleId || '', lessonId || '');

  useSeoMeta({
    title: lesson
      ? `${lesson.number}: ${lesson.title} — Reality Architect`
      : 'Lesson — Reality Architect',
    description: lesson?.coreClaim,
  });

  if (!mod || !lesson) {
    return (
      <div className="container py-16 text-center">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Lesson not found</h1>
        <Link to="/" className="mt-4 inline-flex items-center gap-2 text-purple-600 hover:underline">
          <ArrowLeft className="size-4" />
          Back to Dashboard
        </Link>
      </div>
    );
  }

  const complete = isLessonComplete(lesson.id);
  const reg = REGISTER_STYLES[lesson.register] || REGISTER_STYLES.philosophical;

  const sections = [
    {
      icon: Lightbulb,
      label: 'CONCEPT & ORIGINATOR(S)',
      content: lesson.originators,
      variant: 'default' as const,
    },
    {
      icon: BookOpen,
      label: 'CORE CLAIM',
      content: lesson.coreClaim,
      variant: 'highlight' as const,
    },
    {
      icon: Play,
      label: 'MECHANISM',
      content: lesson.mechanism,
      variant: 'default' as const,
    },
    {
      icon: Eye,
      label: 'CANONICAL EXAMPLE',
      content: lesson.canonicalExample,
      variant: 'example' as const,
    },
    {
      icon: HelpCircle,
      label: 'CONCEPTUAL TENSION',
      content: lesson.conceptualTension,
      variant: 'default' as const,
    },
    {
      icon: LinkIcon,
      label: 'CONNECTION NODE',
      content: lesson.connectionNode,
      variant: 'connection' as const,
    },
    {
      icon: Play,
      label: 'MICRO-PRAXIS',
      content: lesson.microPraxis,
      variant: 'praxis' as const,
    },
    {
      icon: Sparkles,
      label: 'ZEIGARNIK HOOK',
      content: lesson.zeigarnikHook,
      variant: 'hook' as const,
    },
  ];

  const sectionStyles: Record<string, string> = {
    highlight: 'border-l-4 border-l-purple-500 bg-purple-50/50 dark:border-l-purple-400 dark:bg-purple-950/20',
    example: 'border-l-4 border-l-blue-500 bg-blue-50/50 dark:border-l-blue-400 dark:bg-blue-950/20',
    connection: 'border-l-4 border-l-emerald-500 bg-emerald-50/50 dark:border-l-emerald-400 dark:bg-emerald-950/20',
    praxis: 'border-l-4 border-l-amber-500 bg-amber-50/50 dark:border-l-amber-400 dark:bg-amber-950/20',
    hook: 'border-l-4 border-l-rose-500 bg-rose-50/50 italic dark:border-l-rose-400 dark:bg-rose-950/20',
    default: '',
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Top Navigation Bar */}
      <div className="sticky top-0 z-10 border-b bg-white/80 backdrop-blur-sm dark:bg-slate-900/80">
        <div className="container flex items-center justify-between py-3">
          <div className="flex items-center gap-3">
            <Link
              to={`/module/${mod.id}`}
              className="inline-flex items-center gap-1.5 text-sm text-slate-500 transition hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            >
              <ArrowLeft className="size-3.5" />
              M{mod.number}
            </Link>
            <Separator orientation="vertical" className="h-4" />
            <span className="font-mono text-xs font-medium text-slate-400 dark:text-slate-500">
              {lesson.number}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Badge className={cn('font-medium', reg.color)}>{reg.label}</Badge>
            <Button
              variant={complete ? 'default' : 'outline'}
              size="sm"
              onClick={() => toggleLessonComplete(lesson.id)}
              className={cn(
                'gap-1.5',
                complete && 'bg-emerald-600 hover:bg-emerald-700 text-white'
              )}
            >
              {complete ? (
                <>
                  <CheckCircle2 className="size-3.5" />
                  Complete
                </>
              ) : (
                <>
                  <Circle className="size-3.5" />
                  Mark Complete
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Lesson Content */}
      <article className="container py-8 md:py-12">
        <div className="mx-auto max-w-3xl">
          {/* Lesson Header */}
          <header className="mb-10">
            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <span>{mod.title}</span>
            </div>
            <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 dark:text-white md:text-4xl">
              {lesson.title}
            </h1>
            <p className="mt-1 font-medium text-purple-600 dark:text-purple-400">
              {lesson.originators}
            </p>
          </header>

          {/* Lesson Sections */}
          <div className="space-y-6">
            {sections.map((section) => (
              <Card
                key={section.label}
                className={cn(
                  'transition-colors',
                  sectionStyles[section.variant] || ''
                )}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <section.icon className="size-4 text-slate-400 dark:text-slate-500" />
                    <CardDescription className="text-xs font-semibold tracking-wider text-slate-500 dark:text-slate-400">
                      {section.label}
                    </CardDescription>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="leading-relaxed text-slate-700 dark:text-slate-300">
                    {section.content}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Lesson Actions */}
          <div className="mt-10 flex items-center justify-between gap-4 rounded-xl border bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <Link
              to={`/chat/${mod.id}/${lesson.id}`}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-purple-600 to-fuchsia-600 px-4 py-2 text-sm font-medium text-white transition hover:from-purple-700 hover:to-fuchsia-700"
            >
              <Sparkles className="size-4" />
              Discuss with AI Tutor
            </Link>
            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              <span>
                Lesson {adjacent.current ? `${CURRICULUM_FLAT.findIndex(l => l.id === lesson.id) + 1}` : '?'} of 74
              </span>
            </div>
          </div>

          {/* Navigation */}
          <div className="mt-8 grid grid-cols-2 gap-4">
            {adjacent.prev ? (
              <Link
                to={`/module/${adjacent.prev.moduleId}/lesson/${adjacent.prev.id}`}
                className="group flex items-start gap-3 rounded-lg border p-4 transition hover:bg-slate-100 dark:border-slate-800 dark:hover:bg-slate-900"
              >
                <ArrowLeft className="mt-0.5 size-4 shrink-0 text-slate-400 transition group-hover:text-slate-600 dark:group-hover:text-slate-300" />
                <div className="min-w-0 text-left">
                  <p className="text-xs text-slate-500 dark:text-slate-400">Previous</p>
                  <p className="truncate text-sm font-medium text-slate-700 dark:text-slate-300">
                    {adjacent.prev.title}
                  </p>
                </div>
              </Link>
            ) : (
              <div />
            )}
            {adjacent.next ? (
              <Link
                to={`/module/${adjacent.next.moduleId}/lesson/${adjacent.next.id}`}
                className="group flex items-start justify-end gap-3 rounded-lg border p-4 text-right transition hover:bg-slate-100 dark:border-slate-800 dark:hover:bg-slate-900"
              >
                <div className="min-w-0">
                  <p className="text-xs text-slate-500 dark:text-slate-400">Next</p>
                  <p className="truncate text-sm font-medium text-slate-700 dark:text-slate-300">
                    {adjacent.next.title}
                  </p>
                </div>
                <ArrowRight className="mt-0.5 size-4 shrink-0 text-slate-400 transition group-hover:text-slate-600 dark:group-hover:text-slate-300" />
              </Link>
            ) : (
              <div />
            )}
          </div>
        </div>
      </article>
    </div>
  );
};

export default LessonPage;
